import { useContext, useState } from 'react';
import { Flex, Typography } from 'antd';
import Icon from '@ant-design/icons';

import { ReactComponent as Success } from '../../../assets/icons/successful.svg';
import { ReactComponent as SuccessWhite } from '../../../assets/icons/successful-white.svg';
import { ReactComponent as Failure } from '../../../assets/icons/failure.svg';
import { ReactComponent as FailureWhite } from '../../../assets/icons/failure-white.svg';
import { useTheme } from '../../../context/ThemeContext';
import {
  BROADCAST_STATUS_KEYS,
  broadcastDownloadFormatOptions,
  DARK,
  HYPHEN,
} from '../../../utilities/constants';
import { ErrorMessageContext } from '../../../context/ErrorMessageContext';
import { API } from '../../../services';
import {
  generateTimestampedFilename,
  getFormattedDateTimeFromTimestamp,
  sanitizeInput,
} from '../../../utilities/commonFunctions';

import LogsLayout from '../../SafetyAndAnomalyMonitoring/components/LogsLayout';
import { useTableManager } from '../../../hooks/logs/useTableManager';
import { useDownloadManager } from '../../../hooks/logs/useDownloadManager';

const { Text } = Typography;

const LogsContent = () => {
  const { theme } = useTheme();
  const successIcon = theme === DARK ? Success : SuccessWhite;
  const failureIcon = theme === DARK ? Failure : FailureWhite;
  const { addErrorMessage } = useContext(ErrorMessageContext);
  const [searchText, setSearchText] = useState('');
  const columns = [
    {
      title: 'Broadcast ID',
      dataIndex: 'broadcast_id',
      key: 'broadcast_id',
      sort: true,
      filter: true,
    },
    {
      title: 'Trip ID',
      dataIndex: 'trip_id',
      key: 'trip_id',
      sort: true,
      filter: true,
    },
    {
      title: 'Vehicle Number',
      dataIndex: 'registration',
      key: 'registration',
      sort: true,
      filter: true,
    },
    {
      title: 'Device ID',
      dataIndex: 'device_id',
      key: 'device_id',
      sort: true,
      filter: true,
    },
    {
      title: 'Timestamp',
      dataIndex: 'created_timestamp',
      key: 'created_timestamp',
      sort: true,
      render: created_timestamp => {
        return getFormattedDateTimeFromTimestamp(created_timestamp);
      },
    },
    {
      title: 'Message',
      dataIndex: 'text_message',
      key: 'text_message',
      filter: true,
      render: text_message => (
        <div className="ant-table-cell-wrap description-cell">
          {text_message}
        </div>
      ),
    },
    {
      title: 'Frequency',
      dataIndex: 'trigger_frequency',
      key: 'trigger_frequency',
      sort: true,
      filter: true,
    },
    {
      title: 'Sent Status',
      dataIndex: 'broadcast_status',
      key: 'broadcast_status',
      sort: true,
      selectFilter: true,
      selectFilterOptions: Object.entries(BROADCAST_STATUS_KEYS)?.map(
        ([key, value]) => ({
          label: key.charAt(0) + key.slice(1).toLowerCase(),
          value: value.charAt(0) + value.slice(1).toLowerCase(),
        })
      ),
      render: status => {
        if (status === BROADCAST_STATUS_KEYS.SUCCESS) {
          return (
            <Flex gap={8}>
              <Icon component={successIcon} className="status-icon" />
              <Text>Success</Text>
            </Flex>
          );
        } else if (status === BROADCAST_STATUS_KEYS.FAILED) {
          return (
            <Flex gap={8}>
              <Icon component={failureIcon} className="status-icon" />
              <Text>Failed</Text>
            </Flex>
          );
        } else if (status === BROADCAST_STATUS_KEYS.PENDING) {
          return (
            <Flex gap={8}>
              <Icon component={failureIcon} className="status-icon" />
              <Text>Pending</Text>
            </Flex>
          );
        } else {
          return <Text>{`${status}`}</Text>;
        }
      },
    },
    {
      title: 'Sent By',
      dataIndex: 'sent_by',
      key: 'sent_by',
      sort: true,
      filter: true,
    },
  ];

  const {
    logsData,
    setLogsData,
    loading,
    tableParams,
    setTableParams,
    fetchData,
  } = useTableManager(API.getBroadcastLogs);

  const { downloadLoading, handleDownload } = useDownloadManager(
    API.getBroadcastLogsDownload,
    generateTimestampedFilename('broadcast_logs'),
    addErrorMessage,
    broadcastDownloadFormatOptions
  );

  const formatData = response => {
    return response?.results?.map((log, index) => ({
      key:
        tableParams?.pagination?.current * tableParams?.pagination?.pageSize +
        index,
      broadcast_id: log?.broadcast_id || HYPHEN,
      trip_id: log?.trip_id || HYPHEN,
      registration: log?.registration || HYPHEN,
      device_id: log?.device_id || HYPHEN,
      created_timestamp: log?.created_timestamp || HYPHEN,
      text_message: log?.text_message || HYPHEN,
      trigger_frequency: log?.trigger_frequency || HYPHEN,
      broadcast_status: log?.broadcast_status || HYPHEN,
      sent_by: log?.sent_by || HYPHEN,
    }));
  };

  const onSearchTextChange = e => {
    setSearchText(sanitizeInput(e.target.value));
  };

  const handleSearch = () => {
    if (searchText === '') {
      const newFilters = Object.fromEntries(
        Object.entries(tableParams.filters || {}).filter(
          ([key]) => key !== 'trip_id'
        )
      );
      setTableParams({
        ...tableParams,
        filters: newFilters,
        pagination: {
          ...tableParams.pagination,
          current: 1,
        },
      });
    } else {
      setTableParams({
        ...tableParams,
        filters: {
          ...tableParams.filters,
          trip_id: searchText,
        },
        pagination: {
          ...tableParams.pagination,
          current: 1,
        },
      });
    }
  };

  return (
    <>
      <LogsLayout
        logsData={logsData}
        setLogsData={setLogsData}
        loading={loading}
        tableParams={tableParams}
        setTableParams={setTableParams}
        fetchData={fetchData}
        formatData={formatData}
        columns={columns}
        downloadLoading={downloadLoading}
        handleDownload={handleDownload}
        enableTripIdSearch={true}
        onSearchTextChange={onSearchTextChange}
        handleSearch={handleSearch}
      />
    </>
  );
};

export default LogsContent;
